export const technicalWritingModule = {
  id: "tc-technical-writing",
  title: "Technical Writing Excellence",
  description: "Create clear, comprehensive documentation that serves diverse audiences and drives organizational efficiency.",
  content: `
## What You'll Learn
By the end of this module, you will be able to:
- Write technical documentation that serves both expert and novice audiences effectively
- Apply information architecture principles to organize complex content logically
- Use plain language techniques to make technical concepts accessible without losing precision
- Implement documentation standards that improve team productivity and reduce support burden

## Prerequisites
- Experience with your technical domain or subject matter
- Basic understanding of your organization's documentation tools

## Overview
Technical writing is one of the most valuable professional skills in the modern workplace. Clear documentation reduces support requests by up to 40%, accelerates onboarding by 60%, and significantly improves cross-team collaboration. This module teaches enterprise-grade writing techniques used by leading technology companies.

---

## Key Concepts

### Information Architecture for Technical Content
Effective technical writing starts with logical information architecture. Users need to find relevant information quickly, understand it easily, and apply it successfully.

#### Why It Matters
Poor information architecture costs organizations thousands of hours annually in lost productivity. When information is well-organized, teams spend 70% less time searching for answers and make 50% fewer errors during implementation.

#### Best Practices
- Use progressive disclosure to layer complexity appropriately
- Create clear content hierarchies with consistent heading structures
- Implement cross-referencing systems for related information
- Design scannable layouts with bullet points, tables, and visual breaks

### Plain Language Principles
Plain language doesn't mean "dumbed down" language. It means clear, direct communication that respects your audience's time and cognitive load.

#### Industry Usage
Government agencies, healthcare organizations, and technology companies mandate plain language standards because they improve comprehension rates by 85% across diverse audiences while maintaining technical accuracy.

---

## Practical Examples

### Example 1: Before and After - API Documentation
Transform dense technical writing into accessible guidance.

**Before:**
"The authentication mechanism necessitates the implementation of OAuth 2.0 protocols with bearer token authorization wherein the client application must obtain access credentials through the designated endpoint..."

**After:**
"To authenticate API requests:
1. Get your access token from the /auth/token endpoint
2. Include the token in your request header: Authorization: Bearer [your_token]
3. Tokens expire after 24 hours and must be refreshed"

### Example 2: Error Message Optimization
Convert technical errors into actionable guidance.

**Before:** "Database connection timeout error 504"

**After:** "Connection failed: The database is taking too long to respond. Try again in 30 seconds. If the problem continues, contact IT support at ext. 1234."

💡 **Pro Tip**: Use the "Five W's" framework (Who, What, When, Where, Why) to ensure your documentation answers all essential questions your audience might have.

---

## Industry Adoption

### Why Companies Choose This Approach
Organizations with strong technical writing practices report 45% faster product adoption, 60% fewer support tickets, and significantly higher customer satisfaction scores. Technical writing is now considered a core business competency.

### Popular Tools and Libraries
- **GitBook**: Collaborative documentation platform with version control integration
- **Notion**: All-in-one workspace for technical documentation and knowledge management
- **Confluence**: Enterprise documentation platform with advanced collaboration features

### Comparison with Alternatives
| Approach | Advantages | Use Cases |
|----------|------------|-----------|
| Task-Oriented | Direct, actionable, efficient | User guides, procedures, troubleshooting |
| Reference-Style | Comprehensive, searchable, authoritative | API documentation, specifications, standards |

---

## Key Takeaways

✅ **Essential Points**:
- Information architecture determines how quickly users find and apply your content
- Plain language principles improve comprehension without sacrificing technical accuracy
- Consistent formatting and style reduce cognitive load for readers
- User testing validates whether your documentation actually works in practice

⚠️ **Common Pitfalls**:
- Writing for subject matter experts when your audience includes beginners
- Burying important action items within long paragraphs of explanatory text

## Next Steps
Next, you'll learn meeting facilitation skills that ensure your excellent communication translates into productive collaboration and decision-making.

---
`,
  duration: "50 minutes",
  difficulty: "intermediate" as const,
  objectives: [
    "Master information architecture principles",
    "Apply plain language techniques",
    "Create user-centered documentation",
    "Implement scalable writing standards"
  ],
  keyTopics: [
    "Information Architecture",
    "Plain Language Writing",
    "Documentation Standards",
    "User Experience Design",
    "Content Strategy"
  ]
};